import { useCallback, useEffect } from "react";
import { View, Text, Platform } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { SafeAreaView } from "react-native-safe-area-context";
import {
  useLinkTo,
  useNavigation,
  useRoute,
  type RouteProp,
} from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { IHeadlessImpersonationClient } from "@stytch/core/public";
import { useStytch } from "../../utils/useStytch";
import { consumeAuthReturnUrl } from "../../utils/getAuthCallbackUrl";
import type { RootStackParamList } from "../../App";
import { useTranslations } from "@src/packages/i18n";
import LivingCellularBackground from "../../onboarding/components/LivingCellularBackground";
import {
  LouiseMark,
  MovingGradientTitle,
  OnboardingPrimaryButton,
} from "../../onboarding/components/OnboardingLayout";

type AuthCallbackRouteProp = RouteProp<RootStackParamList, "AuthCallback">;

function ExpiredLinkScreen({ onSignIn }: { onSignIn: () => void }) {
  const t = useTranslations();

  return (
    <View style={{ flex: 1, backgroundColor: "#1A0A02" }}>
      <LivingCellularBackground
        motionEnabled={false}
        origin={{ x: 0.5, y: 0.82 }}
      />
      <LinearGradient
        pointerEvents="none"
        colors={[
          "rgba(21, 8, 2, 0.08)",
          "rgba(21, 8, 2, 0.00)",
          "rgba(14, 6, 2, 0.46)",
        ]}
        locations={[0, 0.46, 1]}
        style={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}
      />
      <SafeAreaView style={{ flex: 1 }}>
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            paddingHorizontal: 24,
          }}
        >
          <View
            style={{
              width: "100%",
              maxWidth: 520,
              alignItems: "center",
              padding: 32,
              borderTopLeftRadius: 72,
              borderTopRightRadius: 72,
              borderBottomLeftRadius: 28,
              borderBottomRightRadius: 28,
              backgroundColor: "rgba(16, 11, 8, 0.30)",
              borderWidth: 1,
              borderColor: "rgba(255, 255, 255, 0.12)",
            }}
          >
            <LouiseMark size={72} />
            <MovingGradientTitle
              style={{
                fontFamily: "Tobias_300Light",
                fontSize: 32,
                lineHeight: 36,
                letterSpacing: -0.7,
                color: "#FFFFFF",
                textAlign: "center",
                marginTop: 24,
                marginBottom: 10,
              }}
            >
              {t("onboarding.auth_callback.sign_in_link_expired")}
            </MovingGradientTitle>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                lineHeight: 20,
                color: "rgba(255, 255, 255, 0.72)",
                textAlign: "center",
                marginBottom: 24,
              }}
            >
              {t("onboarding.auth_callback.link_no_longer_valid")}
            </Text>
            <OnboardingPrimaryButton
              title={t("onboarding.auth_callback.go_sign_in")}
              onPress={onSignIn}
              showArrow
            />
          </View>
        </View>
      </SafeAreaView>
    </View>
  );
}

/**
 * Auth callback screen — handles token exchange after returning from
 * a magic link click or OAuth provider redirect.
 */
export default function AuthCallbackScreen() {
  const t = useTranslations();
  const stytch = useStytch();
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const linkTo = useLinkTo();
  const route = useRoute<AuthCallbackRouteProp>();

  const { token, stytch_token_type: tokenType, status } = route.params ?? {};

  const returnToLogin = useCallback(() => {
    if (Platform.OS === "web") {
      consumeAuthReturnUrl();
      const loginPath = "/login?auth_error=oauth";
      window.history.replaceState({}, "", loginPath);
      linkTo(loginPath);
      return;
    }

    navigation.navigate("Onboarding", {
      screen: "Login",
      params: { authError: "oauth" },
    });
  }, [linkTo, navigation]);
  const isExpired = status === "expired";

  useEffect(() => {
    if (isExpired) return;

    const processCallback = async () => {
      if (!token || !tokenType) {
        console.error("AuthCallback: no token or token type provided");
        returnToLogin();
        return;
      }

      try {
        if (tokenType === "magic_links") {
          await stytch.magicLinks.authenticate(token, {
            session_duration_minutes: 60 * 24, // 24 hours
          });
        } else if (tokenType === "oauth") {
          await stytch.oauth.authenticate(token, {
            session_duration_minutes: 60 * 24, // 24 hours
          });
        } else if (tokenType === "impersonation") {
          const { impersonation } = stytch as unknown as {
            impersonation: IHeadlessImpersonationClient;
          };
          await impersonation.authenticate({
            impersonation_token: token,
          });
        } else {
          console.warn(`AuthCallback: unknown token type "${tokenType}"`);
          returnToLogin();
          return;
        }

        if (Platform.OS === "web") {
          const returnTo = consumeAuthReturnUrl();
          if (returnTo) {
            window.history.replaceState({}, "", returnTo);
            linkTo(returnTo);
          } else {
            window.history.replaceState({}, "", "/");
            navigation.navigate("Home");
          }
        } else {
          navigation.navigate("Home");
        }
      } catch (err) {
        console.error("AuthCallback: authentication failed", err);
        returnToLogin();
      }
    };

    processCallback();
  }, [isExpired, returnToLogin, stytch, navigation, linkTo, token, tokenType]);

  if (isExpired) {
    return (
      <ExpiredLinkScreen
        onSignIn={() => navigation.navigate("Onboarding", { screen: "Login" })}
      />
    );
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: "#FDF8F4",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Text style={{ color: "#8C7B6B", fontSize: 14 }}>
        {t("onboarding.auth_callback.completing_sign_in")}
      </Text>
    </View>
  );
}
